import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Widget,
  WidgetHeader,
  WidgetContent,
} from "@/components/consultation/shared/Widget";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";

import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsMutating } from "@tanstack/react-query";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EnergySummaryCard } from "@/components/consultation/tabs/nutrition/components/energy-summary-card";
import { FeaturedNutrientsCard } from "@/components/consultation/tabs/nutrition/components/featured-nutrients-card";
import { DetailedNutrientsPanel } from "@/components/consultation/tabs/nutrition/components/detailed-nutrients-panel";
import { AddFoodDialog } from "@/components/consultation/tabs/nutrition/components/add-food-dialog";
import { AddNoteDialog } from "@/components/consultation/tabs/nutrition/components/add-note-dialog";
import { NoteRow } from "@/components/consultation/tabs/nutrition/components/note-row";
import { NutrientReadings } from "@/components/consultation/tabs/nutrition/components/nutrient-readings";
import {
  AddFeedDialog,
  TubeFeedingSection,
} from "@/components/consultation/tabs/nutrition/components/tube-feeding-section";
import {
  MEAL_TIME_DISPLAY,
  quantityPatternFor,
  computeEntryKcal,
} from "@/components/consultation/tabs/nutrition/components/meal-constants";
import {
  Apple,
  Loader2,
  Plus,
  Trash2,
  StickyNote,
  Copy,
  MoveVertical,
  AlertCircle,
  AlertTriangle,
  Flame,
  MoreVertical,
  Check,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useNutritionEvaluationContext } from "../NutritionEvaluationContext";
import {
  useMealPlanData,
  useCopyDietRecallToMealPlan,
  useCopyMealPlanOption,
  useAddMealEntry,
  useUpdateMealEntry,
  useDeleteMealEntry,
  useMoveNutritionEntry,
  useFoodServings,
  useNutritionTarget,
  useDietRecall,
  useHealthProfile,
} from "@/hooks/useNutritionEvaluation";
import type {
  MealData,
  MealTime,
  MealEntry,
  FeedData,
  FeedTime,
  FeedEntry,
} from "@/types/nutrition";
import type {
  MoveTarget,
  MoveTargetContext,
} from "@/lib/nutrition-evaluation-api";
import { toast } from "sonner";

// =============================================================================
// Plan options
// =============================================================================

const PLAN_OPTIONS = [
  { value: "0", label: "Plan A" },
  { value: "1", label: "Plan B" },
  { value: "2", label: "Plan C" },
];

// Cross-plan "Copy to" target. `groups: null` means the target plan's
// meal groups haven't loaded yet (lazy fetch).
type CrossPlanTarget = {
  planIndex: number;
  planLabel: string;
  groups: MealTime[] | null;
};

// =============================================================================
// Meal Entry Row
// =============================================================================

interface MealEntryRowProps {
  entry: MealEntry | FeedEntry;
  appointmentId: string;
  isTubeFeeding: boolean;
  isCompleted: boolean;
  mealTimes?: MealTime[];
  feedGroups?: FeedTime[];
  planIndex?: number;
  mealGroupLabel?: string;
  /** Tube-feeding meal-plan rows historically computed kcal with net carbs. */
  useNetCarbs?: boolean;
  /** Other plans the row can copy into (oral meal plan only). */
  crossPlanTargets?: CrossPlanTarget[];
  externalMoveTargets?: MoveTarget[];
  /** Fired the first time the row's actions menu opens — triggers lazy
   *  fetch of other plans' meal-group structure for cross-plan "Copy to". */
  onActionsMenuOpen?: () => void;
}

const MealEntryRow: React.FC<MealEntryRowProps> = ({
  entry,
  appointmentId,
  isTubeFeeding,
  isCompleted,
  mealTimes = [],
  feedGroups = [],
  mealGroupLabel,
  useNetCarbs = false,
  crossPlanTargets,
  externalMoveTargets = [],
  onActionsMenuOpen,
}) => {
  const isFeedItem = isTubeFeeding && !!(entry as FeedEntry).feed_item;
  const parentGroupId =
    (entry as MealEntry).meal_group ?? (entry as FeedEntry).feed_group;

  const serverEditValue = isFeedItem
    ? (entry as FeedEntry).volume_ml?.toString() || ""
    : entry.quantity?.toString() || "";
  const [localEditValue, setLocalEditValue] = useState(serverEditValue);
  const [isDirty, setIsDirty] = useState(false);
  const [servingOpen, setServingOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const deleteEntry = useDeleteMealEntry(appointmentId, isTubeFeeding, true);
  const updateEntry = useUpdateMealEntry(appointmentId, isTubeFeeding, true);
  const addEntry = useAddMealEntry(appointmentId, isTubeFeeding, true);
  const { mutate: moveEntryMutate, isPending: isMovePending } =
    useMoveNutritionEntry(appointmentId);

  useEffect(() => {
    if (!isDirty) setLocalEditValue(serverEditValue);
  }, [serverEditValue, isDirty]);
  const { data: servings = [] } = useFoodServings(
    entry.food_item || 0,
    !!entry.food_item && servingOpen && !isFeedItem
  );

  // Tube feeds are limited to one food entry per feed, so skip any target feed
  // group that already has one — Copy/Move would otherwise bypass the
  // "Add food" disabled state enforced in tube-feeding-section.
  const copyToTargets = isTubeFeeding
    ? feedGroups.filter(
        (fg) =>
          fg.id !== parentGroupId &&
          !fg.entries.some(
            (e) =>
              !e.is_comment &&
              (e.food_item || e.food_item_details || e.feed_item_details)
          )
      )
    : mealTimes.filter((mt) => mt.id !== parentGroupId);

  const sameContextMoveTargets = useMemo<MoveTarget[]>(
    () =>
      copyToTargets.map((target) => ({
        id: target.id,
        context: "meal_plan" as MoveTargetContext,
        label: isTubeFeeding
          ? (target as FeedTime).label || (target as FeedTime).time
          : MEAL_TIME_DISPLAY[(target as MealTime).meal_time] ||
            (target as MealTime).meal_time,
      })),
    [copyToTargets, isTubeFeeding]
  );

  const hasMoveTargets =
    sameContextMoveTargets.length > 0 ||
    externalMoveTargets.length > 0 ||
    Boolean(crossPlanTargets?.length);

  const hasCopyTargets =
    copyToTargets.length > 0 || Boolean(crossPlanTargets?.length);

  const hasExternalMoveTargets = externalMoveTargets.length > 0;
  const hasCrossPlanMoveTargets = Boolean(crossPlanTargets?.length);

  const buildCopyPayload = useCallback(
    (targetGroupId: string): Parameters<typeof addEntry.mutate>[0] | null => {
      if (isTubeFeeding) {
        if (isFeedItem) {
          const feedEntry = entry as FeedEntry;
          if (!feedEntry.feed_item || !feedEntry.volume_ml) return null;
          return {
            feed_group: targetGroupId,
            feed_item: feedEntry.feed_item,
            volume_ml: feedEntry.volume_ml,
          } as Parameters<typeof addEntry.mutate>[0];
        }
        if (!entry.food_item || !entry.serving_size) return null;
        return {
          feed_group: targetGroupId,
          food_item: entry.food_item,
          serving_size: entry.serving_size,
          quantity: entry.quantity,
        } as Parameters<typeof addEntry.mutate>[0];
      }
      if (!entry.food_item || !entry.serving_size) return null;
      return {
        meal_group: targetGroupId,
        food_item: entry.food_item,
        serving_size: entry.serving_size,
        quantity: entry.quantity,
      } as Parameters<typeof addEntry.mutate>[0];
    },
    [addEntry, entry, isFeedItem, isTubeFeeding]
  );

  const handleCopyTo = useCallback(
    (targetGroupId: string, successToast?: string) => {
      const payload = buildCopyPayload(targetGroupId);
      if (!payload) return;
      addEntry.mutate(
        payload,
        successToast
          ? { onSuccess: () => toast.success(successToast) }
          : undefined
      );
    },
    [addEntry, buildCopyPayload]
  );

  const handleMoveTo = useCallback(
    (target: MoveTarget) => {
      moveEntryMutate(
        {
          entry_type: isTubeFeeding ? "feed" : "meal",
          source_context: "meal_plan",
          target_context: target.context,
          source_entry_id: entry.id,
          target_group_id: target.id,
        },
        {
          onSuccess: () => toast.success("Moved successfully"),
          onError: (error) =>
            toast.error(
              (error as { message?: string })?.message ||
                "Move failed. The item was not changed."
            ),
        }
      );
    },
    [entry.id, isTubeFeeding, moveEntryMutate]
  );

  const handleServingChange = useCallback(
    (value: string) => {
      const servingId = parseInt(value);
      if (!Number.isNaN(servingId) && servingId !== entry.serving_size) {
        updateEntry.mutate(
          {
            entryId: entry.id,
            data: { serving_size: servingId },
          },
          {
            onSuccess: () => toast.success("Serving size updated"),
            onError: () => toast.error("Failed to update serving size"),
          }
        );
      }
    },
    [entry.id, entry.serving_size, updateEntry]
  );

  const servingLabel =
    entry.serving_details?.description ||
    entry.serving_details?.serving_unit?.name ||
    "Serving";

  // Local edit only — the API call fires when the user commits via the
  // ✅ button (or Enter). Avoids per-keystroke recalculation on the backend.
  // Whole servings only when the entry's food contains something indivisible.
  // Stated by the API on food_item_details — never inferred here from the food
  // being a supplement, since a weighed whey scoop halves perfectly well.
  const wholeServingsOnly = Boolean(
    (
      entry.food_item_details as
        | { requires_whole_servings?: boolean }
        | undefined
    )?.requires_whole_servings
  );

  const handleEditValueChange = useCallback(
    (value: string) => {
      if (!quantityPatternFor(wholeServingsOnly).test(value)) return;
      setLocalEditValue(value);
      setIsDirty(value !== serverEditValue);
    },
    [serverEditValue, wholeServingsOnly]
  );

  const handleEditValueCommit = useCallback(() => {
    if (!isDirty) return;
    if (localEditValue === "" || localEditValue.endsWith(".")) {
      setLocalEditValue(serverEditValue);
      setIsDirty(false);
      return;
    }
    const data = isFeedItem
      ? { volume_ml: localEditValue }
      : { quantity: localEditValue };
    updateEntry.mutate(
      { entryId: entry.id, data },
      { onSuccess: () => setIsDirty(false) }
    );
  }, [
    isDirty,
    isFeedItem,
    localEditValue,
    serverEditValue,
    entry.id,
    updateEntry,
  ]);

  const handleEditValueCancel = useCallback(() => {
    setLocalEditValue(serverEditValue);
    setIsDirty(false);
  }, [serverEditValue]);

  const handleConfirmDelete = useCallback(() => {
    deleteEntry.mutate(entry.id, {
      onSuccess: () => setShowDeleteConfirm(false),
    });
  }, [deleteEntry, entry.id]);

  if (entry.is_comment) {
    return (
      <NoteRow
        entryId={entry.id}
        comment={entry.comment ?? ""}
        isAttached={false}
        appointmentId={appointmentId}
        isTubeFeeding={isTubeFeeding}
        isMealPlan={true}
        isCompleted={isCompleted}
        mealGroupLabel={mealGroupLabel}
      />
    );
  }

  const itemLabel = isFeedItem
    ? (entry as FeedEntry).feed_item_details?.name ||
      `Feed #${(entry as FeedEntry).feed_item}`
    : entry.food_item_details?.name || `Food #${entry.food_item}`;
  const attachedComment =
    isTubeFeeding && entry.comment?.trim() ? entry.comment.trim() : "";

  return (
    <>
      <div className="grid grid-cols-[16px_1fr_104px_220px_80px_32px] items-center gap-3 px-3 py-2 hover:bg-muted/30 rounded-lg transition-colors group">
        <Apple className="size-4 fill-primary text-primary" />
        <div className="min-w-0">
          <div className="z-body break-words leading-snug">{itemLabel}</div>
        </div>
        {!isCompleted ? (
          <div className="flex items-center gap-1">
            <Input
              type="text"
              value={localEditValue}
              onChange={(e) => handleEditValueChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleEditValueCommit();
                if (e.key === "Escape") handleEditValueCancel();
              }}
              className={cn(
                "h-9 px-2 text-center flex-1 min-w-0",
                isDirty && "border-primary ring-2 ring-primary/20 bg-primary/5"
              )}
              disabled={updateEntry.isPending}
            />
            {isDirty && (
              <Button
                type="button"
                size="icon-sm"
                onClick={handleEditValueCommit}
                disabled={updateEntry.isPending}
                aria-label={isFeedItem ? "Save volume" : "Save quantity"}
              >
                {updateEntry.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Check className="h-3.5 w-3.5" />
                )}
              </Button>
            )}
          </div>
        ) : (
          <span className="z-body-md text-center">
            {isFeedItem ? (entry as FeedEntry).volume_ml : entry.quantity}
          </span>
        )}
        {isFeedItem ? (
          <span className="z-body text-muted-foreground">ml</span>
        ) : !isCompleted ? (
          <Select
            open={servingOpen}
            onOpenChange={setServingOpen}
            value={entry.serving_size?.toString() ?? ""}
            onValueChange={handleServingChange}
            disabled={updateEntry.isPending}
          >
            <SelectTrigger className="data-[size=default]:h-auto min-h-9 w-full whitespace-normal py-1.5 text-left [&>[data-slot=select-value]]:line-clamp-none [&>[data-slot=select-value]]:text-left">
              <SelectValue placeholder="Serving">{servingLabel}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {servings.map((serving) => (
                <SelectItem key={serving.id} value={serving.id.toString()}>
                  {serving.description ||
                    serving.serving_unit?.name ||
                    "Serving"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span className="z-body text-muted-foreground break-words">
            {servingLabel}
          </span>
        )}
        <div className="z-body-md text-right z-numeric flex items-center justify-end gap-1.5">
          {updateEntry.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          ) : (
            <span>
              {Math.round(
                computeEntryKcal(
                  entry,
                  useNetCarbs ? { useNetCarbs: true } : undefined
                )
              ) || "—"}{" "}
              kcal
            </span>
          )}
        </div>
        {!isCompleted ? (
          <DropdownMenu
            onOpenChange={(open) => {
              if (open) onActionsMenuOpen?.();
            }}
          >
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" aria-label="Row actions">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {(hasCopyTargets || hasMoveTargets) &&
              ((isFeedItem &&
                (entry as FeedEntry).feed_item &&
                (entry as FeedEntry).volume_ml) ||
                (!isFeedItem && entry.food_item && entry.serving_size)) ? (
                <>
                  {hasCopyTargets && (
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Copy className="h-4 w-4" />
                        Copy to
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        {copyToTargets.map((target) => (
                          <DropdownMenuItem
                            key={target.id}
                            onSelect={() => handleCopyTo(target.id)}
                          >
                            {isTubeFeeding
                              ? (target as FeedTime).label ||
                                (target as FeedTime).time
                              : MEAL_TIME_DISPLAY[
                                  (target as MealTime).meal_time
                                ] || (target as MealTime).meal_time}
                          </DropdownMenuItem>
                        ))}
                        {crossPlanTargets && crossPlanTargets.length > 0 ? (
                          <>
                            {copyToTargets.length > 0 && (
                              <DropdownMenuSeparator />
                            )}
                            {crossPlanTargets.map((plan) => (
                              <DropdownMenuSub key={plan.planIndex}>
                                <DropdownMenuSubTrigger>
                                  {plan.planLabel}
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent>
                                  {plan.groups === null ? (
                                    <DropdownMenuItem disabled>
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                      Loading…
                                    </DropdownMenuItem>
                                  ) : plan.groups.length === 0 ? (
                                    <DropdownMenuItem disabled>
                                      No meal groups
                                    </DropdownMenuItem>
                                  ) : (
                                    plan.groups.map((mt) => {
                                      const groupLabel =
                                        MEAL_TIME_DISPLAY[mt.meal_time] ||
                                        mt.meal_time;
                                      return (
                                        <DropdownMenuItem
                                          key={mt.id}
                                          onSelect={() =>
                                            handleCopyTo(
                                              mt.id,
                                              `Copied to ${plan.planLabel} → ${groupLabel}`
                                            )
                                          }
                                        >
                                          {groupLabel}
                                        </DropdownMenuItem>
                                      );
                                    })
                                  )}
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                            ))}
                          </>
                        ) : null}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  )}
                  {hasMoveTargets && (
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <MoveVertical className="h-4 w-4" />
                        Move to
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        {sameContextMoveTargets.map((target) => (
                          <DropdownMenuItem
                            key={`${target.context}-${target.id}`}
                            onSelect={() => handleMoveTo(target)}
                            disabled={isMovePending}
                          >
                            {target.label}
                          </DropdownMenuItem>
                        ))}
                        {hasExternalMoveTargets && (
                          <>
                            {sameContextMoveTargets.length > 0 && (
                              <DropdownMenuSeparator />
                            )}
                            <DropdownMenuSub>
                              <DropdownMenuSubTrigger>
                                Diet Recall
                              </DropdownMenuSubTrigger>
                              <DropdownMenuSubContent>
                                {externalMoveTargets.map((target) => (
                                  <DropdownMenuItem
                                    key={`${target.context}-${target.id}`}
                                    onSelect={() => handleMoveTo(target)}
                                    disabled={isMovePending}
                                  >
                                    {target.label}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuSubContent>
                            </DropdownMenuSub>
                          </>
                        )}
                        {hasCrossPlanMoveTargets && crossPlanTargets ? (
                          <>
                            {(sameContextMoveTargets.length > 0 ||
                              hasExternalMoveTargets) && (
                              <DropdownMenuSeparator />
                            )}
                            {crossPlanTargets.map((plan) => (
                              <DropdownMenuSub key={plan.planIndex}>
                                <DropdownMenuSubTrigger>
                                  {plan.planLabel}
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent>
                                  {plan.groups === null ? (
                                    <DropdownMenuItem disabled>
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                      Loading...
                                    </DropdownMenuItem>
                                  ) : plan.groups.length === 0 ? (
                                    <DropdownMenuItem disabled>
                                      No meal groups
                                    </DropdownMenuItem>
                                  ) : (
                                    plan.groups.map((mt) => {
                                      const groupLabel =
                                        MEAL_TIME_DISPLAY[mt.meal_time] ||
                                        mt.meal_time;
                                      return (
                                        <DropdownMenuItem
                                          key={mt.id}
                                          onSelect={() =>
                                            handleMoveTo({
                                              id: mt.id,
                                              context: "meal_plan",
                                              label: groupLabel,
                                            })
                                          }
                                          disabled={isMovePending}
                                        >
                                          {groupLabel}
                                        </DropdownMenuItem>
                                      );
                                    })
                                  )}
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                            ))}
                          </>
                        ) : null}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  )}
                  <DropdownMenuSeparator />
                </>
              ) : null}
              <DropdownMenuItem
                variant="destructive"
                onSelect={(e) => {
                  e.preventDefault();
                  setShowDeleteConfirm(true);
                }}
                disabled={deleteEntry.isPending}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <span />
        )}
      </div>
      {attachedComment && (
        <NoteRow
          entryId={entry.id}
          comment={attachedComment}
          isAttached
          appointmentId={appointmentId}
          isTubeFeeding={isTubeFeeding}
          isMealPlan={true}
          isCompleted={isCompleted}
          mealGroupLabel={mealGroupLabel}
        />
      )}
      <DeleteEntryConfirm
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
        isLoading={deleteEntry.isPending}
        itemLabel={itemLabel}
        mealGroupLabel={mealGroupLabel}
      />
    </>
  );
};

// =============================================================================
// Delete entry confirmation dialog
// =============================================================================

interface DeleteEntryConfirmProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  itemLabel: string;
  mealGroupLabel?: string;
}

const DeleteEntryConfirm: React.FC<DeleteEntryConfirmProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading,
  itemLabel,
  mealGroupLabel,
}) => {
  const fromPart = mealGroupLabel ? `"${mealGroupLabel}"` : "the food group";
  return (
    <AlertDialog open={open} onOpenChange={onClose}>
      <AlertDialogContent className="border-border/60">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Remove the food item from {fromPart}?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <ul className="list-disc pl-5">
              <li>{itemLabel}</li>
            </ul>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <Button
            type="button"
            variant="destructive"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onConfirm();
            }}
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

// =============================================================================
// Meal Time Card
// =============================================================================

interface MealTimeCardProps {
  mealTime: MealTime;
  index: number;
  appointmentId: string;
  isTubeFeeding: boolean;
  isCompleted: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  planIndex: number;
  mealTimes?: MealTime[];
  crossPlanTargets?: CrossPlanTarget[];
  externalMoveTargets?: MoveTarget[];
  onActionsMenuOpen?: () => void;
}

const MealTimeCard: React.FC<MealTimeCardProps> = ({
  mealTime,
  index: _index,
  appointmentId,
  isTubeFeeding,
  isCompleted,
  isExpanded,
  onToggle,
  planIndex,
  mealTimes = [],
  crossPlanTargets,
  externalMoveTargets = [],
  onActionsMenuOpen,
}) => {
  const [showAddFood, setShowAddFood] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);

  // Energy displayed in the group header is recomputed via Atwater 2-factor
  // from the summary macros (or summed per-entry as a fallback) so it
  // matches the per-row kcal, the pie centre and the targets bar.
  const summary = mealTime.meal_nutrients_summary;
  const mealTimeLabel =
    MEAL_TIME_DISPLAY[mealTime.meal_time] || mealTime.meal_time;
  const hasFoodEntries = mealTime.entries.some((e) => !e.is_comment);
  // Sum per-entry kcal rather than using `summary.energy` — the backend's
  // meal_nutrients_summary often omits `fiber`, which makes a net-carbs
  // Atwater on the summary diverge from the row totals (which use the
  // full per-entry profile that does include fiber).
  const headerEnergy = useMemo(
    () => mealTime.entries.reduce((sum, e) => sum + computeEntryKcal(e), 0),
    [mealTime.entries]
  );
  const mealGroupOptions = useMemo(
    () =>
      mealTimes.map((mt) => ({
        id: mt.id,
        label: MEAL_TIME_DISPLAY[mt.meal_time] || mt.meal_time,
      })),
    [mealTimes]
  );

  return (
    <>
      <Widget>
        <Collapsible open={isExpanded} onOpenChange={onToggle}>
          <WidgetHeader
            title={mealTimeLabel}
            collapsible
            expanded={isExpanded}
            onToggle={onToggle}
            extras={
              hasFoodEntries ? (
                <NutrientReadings
                  energy={headerEnergy}
                  protein={summary?.protein}
                  carbs={summary?.carbs}
                  fat={summary?.fat}
                />
              ) : null
            }
          />

          <CollapsibleContent>
            <WidgetContent
              className={mealTime.entries.length === 0 ? "py-2" : undefined}
            >
              {mealTime.entries.length === 0 ? null : (
                <>
                  <Separator className="mb-4" />
                  <div className="space-y-2">
                    {mealTime.entries
                      .sort((a, b) => a.order - b.order)
                      .map((entry) => (
                        <MealEntryRow
                          key={entry.id}
                          entry={entry}
                          appointmentId={appointmentId}
                          isTubeFeeding={isTubeFeeding}
                          isCompleted={isCompleted}
                          mealTimes={mealTimes}
                          planIndex={planIndex}
                          mealGroupLabel={mealTimeLabel}
                          crossPlanTargets={crossPlanTargets}
                          externalMoveTargets={externalMoveTargets}
                          onActionsMenuOpen={onActionsMenuOpen}
                        />
                      ))}
                  </div>
                </>
              )}

              {!isCompleted && (
                <div
                  className={cn(
                    "flex items-center gap-2",
                    mealTime.entries.length === 0
                      ? "justify-between"
                      : "justify-end pt-3"
                  )}
                >
                  {mealTime.entries.length === 0 && (
                    <span className="z-body text-muted-foreground">
                      No items added yet
                    </span>
                  )}
                  <div className="flex gap-2">
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon-sm"
                            onClick={() => setShowAddFood(true)}
                            aria-label="Add food"
                          >
                            <Plus />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Add food</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon-sm"
                            onClick={() => setShowAddNote(true)}
                            aria-label="Add note"
                          >
                            <StickyNote />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Add note</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              )}
            </WidgetContent>
          </CollapsibleContent>
        </Collapsible>
      </Widget>

      <AddFoodDialog
        open={showAddFood}
        onClose={() => setShowAddFood(false)}
        mealGroupId={mealTime.id}
        mealTimeLabel={mealTimeLabel}
        appointmentId={appointmentId}
        isTubeFeeding={isTubeFeeding}
        isMealPlan={true}
        planIndex={planIndex}
        mealGroupOptions={mealGroupOptions}
      />
      <AddNoteDialog
        open={showAddNote}
        onClose={() => setShowAddNote(false)}
        mealGroupId={mealTime.id}
        mealTimeLabel={mealTimeLabel}
        appointmentId={appointmentId}
        isTubeFeeding={isTubeFeeding}
        isMealPlan={true}
      />
    </>
  );
};

// =============================================================================
// Copy from Diet Recall Dialog (MealPlan-specific)
// =============================================================================

interface CopySourceDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  sourceLabel: string;
  sourceNutrients: {
    energy: number;
    protein: number;
    carbs: number;
    fat: number;
  };
}

const CopySourceDialog: React.FC<CopySourceDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading,
  sourceLabel,
  sourceNutrients,
}) => {
  const { energy, protein, carbs, fat } = sourceNutrients;

  const proteinKcal = protein * 4;
  const carbsKcal = carbs * 4;
  const fatKcal = fat * 9;
  const total = proteinKcal + carbsKcal + fatKcal;

  const getPercentage = (kcal: number) =>
    total > 0 ? ((kcal / total) * 100).toFixed(1) : "0";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] flex flex-col p-0 gap-0 overflow-hidden border-border/60">
        <DialogHeader className="p-6 pb-0 shrink-0">
          <DialogTitle className="flex items-center gap-2 z-h2 text-foreground">
            <Copy className="h-5 w-5 text-primary" />
            Copy from {sourceLabel}
          </DialogTitle>
        </DialogHeader>
        <Separator className="mt-4" />

        <div className="flex-1 overflow-y-auto p-6 bg-background space-y-4">
          <p className="z-body text-muted-foreground">
            This will append all food items from {sourceLabel} into this meal
            plan. Existing items will not be removed — duplicates may appear if
            the same item already exists.
          </p>

          <div className="p-4 bg-muted/50 rounded-lg space-y-3">
            <div className="z-h3 text-foreground">{sourceLabel} Summary:</div>

            <div className="flex items-center justify-center gap-2">
              <Flame className="h-5 w-5 text-primary" />
              <span className="z-h2 text-foreground">{energy.toFixed(0)}</span>
              <span className="text-muted-foreground">kcal</span>
            </div>

            <div className="h-3 rounded-full overflow-hidden flex">
              <div
                className="bg-primary transition-all"
                style={{ width: `${getPercentage(proteinKcal)}%` }}
              />
              <div
                className="bg-secondary transition-all"
                style={{ width: `${getPercentage(carbsKcal)}%` }}
              />
              <div
                className="bg-accent transition-all"
                style={{ width: `${getPercentage(fatKcal)}%` }}
              />
            </div>

            <div className="grid grid-cols-3 gap-2 z-caption">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span>Protein: {protein.toFixed(1)}g</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-secondary" />
                <span>Carbs: {carbs.toFixed(1)}g</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-accent" />
                <span>Fat: {fat.toFixed(1)}g</span>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="p-6 mt-auto border-t border-border/60 bg-background">
          <Button
            variant="outline"
            onClick={onClose}
            className="text-foreground border-border/60"
          >
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Copy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// Meal Plan Content (per-plan-index orchestration)
// =============================================================================

interface MealPlanContentProps {
  planIndex: number;
}

const MealPlanContent: React.FC<MealPlanContentProps> = ({ planIndex }) => {
  const {
    appointmentId,
    isTubeFeeding,
    isCompleted,
    isFeedingTypeReady,
    currentFeedingType,
    dietRecallData,
  } = useNutritionEvaluationContext();

  const [showAddFeed, setShowAddFeed] = useState(false);
  const [expandedMealTimes, setExpandedMealTimes] = useState<
    Record<number, boolean>
  >({});
  // Cross-plan "Copy to" targets are loaded lazily — only after the user
  // first opens a row's actions menu. Keeps initial tab load cheap when the
  // nutritionist never reaches for the copy-across-plans affordance.
  const [hasOpenedActionsMenu, setHasOpenedActionsMenu] = useState(false);
  const handleActionsMenuOpen = useCallback(
    () => setHasOpenedActionsMenu(true),
    []
  );

  const { data: mealPlanData, isLoading } = useMealPlanData(
    appointmentId,
    planIndex,
    isTubeFeeding
  );

  // Other plans' meal-group structure for cross-plan copy. Active plan's
  // call shares cache with `mealPlanData` above. Tube feeding is single-plan,
  // so these stay disabled in that mode. React Query dedupes by query key.
  const isOral = !isTubeFeeding;
  const { data: planAData } = useMealPlanData(
    appointmentId,
    0,
    false,
    isOral && (planIndex === 0 || hasOpenedActionsMenu)
  );
  const { data: planBData } = useMealPlanData(
    appointmentId,
    1,
    false,
    isOral && (planIndex === 1 || hasOpenedActionsMenu)
  );
  const { data: planCData } = useMealPlanData(
    appointmentId,
    2,
    false,
    isOral && (planIndex === 2 || hasOpenedActionsMenu)
  );

  const crossPlanTargets = useMemo<CrossPlanTarget[]>(() => {
    if (!isOral) return [];
    const all: {
      planIndex: number;
      planLabel: string;
      data: MealData | null;
    }[] = [
      { planIndex: 0, planLabel: "Plan A", data: planAData as MealData | null },
      { planIndex: 1, planLabel: "Plan B", data: planBData as MealData | null },
      { planIndex: 2, planLabel: "Plan C", data: planCData as MealData | null },
    ];
    return all
      .filter((p) => p.planIndex !== planIndex)
      .map((p) => ({
        planIndex: p.planIndex,
        planLabel: p.planLabel,
        groups: p.data?.meal_groups
          ? [...p.data.meal_groups].sort((a, b) => a.order - b.order)
          : null,
      }));
  }, [isOral, planIndex, planAData, planBData, planCData]);

  const dietRecallMoveTargets = useMemo<MoveTarget[]>(() => {
    if (isTubeFeeding) {
      const feedGroups = (dietRecallData as FeedData | null)?.feed_groups;
      if (!feedGroups) return [];
      return feedGroups
        .filter(
          (group) =>
            !group.entries.some(
              (entry) =>
                !entry.is_comment &&
                (entry.food_item ||
                  entry.food_item_details ||
                  entry.feed_item_details)
            )
        )
        .map((group) => ({
          id: group.id,
          context: "diet_recall" as MoveTargetContext,
          label: group.label || group.time,
        }));
    }

    const groups = (dietRecallData as MealData | null)?.meal_groups;
    if (!groups) return [];
    return [...groups]
      .sort((a, b) => a.order - b.order)
      .map((group) => ({
        id: group.id,
        context: "diet_recall" as MoveTargetContext,
        label: MEAL_TIME_DISPLAY[group.meal_time] || group.meal_time,
      }));
  }, [dietRecallData, isTubeFeeding]);

  const { data: nutritionTarget } = useNutritionTarget(appointmentId);
  const { data: healthProfile } = useHealthProfile(appointmentId);

  // Any in-flight nutrition mutation blocks user input via the overlay below
  // so quick clicks don't double-fire add / move / delete operations.
  const pendingMutationCount = useIsMutating();
  const isMutating = pendingMutationCount > 0;

  const toggleMealTime = useCallback((index: number) => {
    setExpandedMealTimes((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  }, []);

  // SetTargetTab persists the kcal target into `recommended_calories`. The
  // legacy `energy` field is filled with an unrelated value (default "0")
  // and must not be used as the calorie target — fall back only if the
  // newer field is missing.
  const targets = useMemo(
    () => ({
      energy: nutritionTarget?.recommended_calories ?? nutritionTarget?.energy,
      carbs: nutritionTarget?.carbs,
      protein: nutritionTarget?.protein,
      fiber: nutritionTarget?.fibre,
      fats: nutritionTarget?.fat,
    }),
    [nutritionTarget]
  );

  const mealData = isTubeFeeding ? null : (mealPlanData as MealData);
  const feedData = isTubeFeeding ? (mealPlanData as FeedData) : null;

  const mealTimes = useMemo(() => {
    if (mealData?.meal_groups && mealData.meal_groups.length > 0) {
      return [...mealData.meal_groups].sort((a, b) => a.order - b.order);
    }
    return [];
  }, [mealData]);

  const hasFoodEntries = useMemo(() => {
    if (isTubeFeeding && feedData?.feed_groups) {
      return feedData.feed_groups.some((group) =>
        group.entries?.some((entry) => entry.food_item)
      );
    }
    if (mealTimes.length > 0) {
      return mealTimes.some((mealTime) =>
        mealTime.entries?.some((entry) => !entry.is_comment)
      );
    }
    return false;
  }, [isTubeFeeding, feedData, mealTimes]);

  if (!isFeedingTypeReady || isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const totalNutrients =
    mealData?.total_nutrients || feedData?.total_nutrients || null;

  return (
    <div className="relative space-y-6">
      {isMutating && (
        <div className="absolute inset-0 z-50 flex items-start justify-center pt-4 bg-background/40 backdrop-blur-[1px] cursor-wait">
          <div className="flex items-center gap-2 rounded-full border border-border/60 bg-background px-4 py-2 shadow-sm">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <span className="z-body text-foreground">Saving…</span>
          </div>
        </div>
      )}

      {!isCompleted && isTubeFeeding && (
        <div className="flex items-center justify-end">
          <Button onClick={() => setShowAddFeed(true)}>
            <Plus />
            Add Feed
          </Button>
        </div>
      )}

      {isTubeFeeding ? (
        <TubeFeedingSection
          feedData={feedData}
          appointmentId={appointmentId}
          isCompleted={isCompleted}
          isMealPlan={true}
          planIndex={planIndex}
          allowDeleteEmptyFeed
          renderEntry={({ entry, feedGroups, mealGroupLabel }) => (
            <MealEntryRow
              key={entry.id}
              entry={entry}
              appointmentId={appointmentId}
              isTubeFeeding={true}
              isCompleted={isCompleted}
              feedGroups={feedGroups}
              mealGroupLabel={mealGroupLabel}
              planIndex={planIndex}
              useNetCarbs
              externalMoveTargets={dietRecallMoveTargets}
            />
          )}
        />
      ) : (
        <div className="space-y-2">
          {mealTimes.length > 0 ? (
            mealTimes.map((mealTime, index) => (
              <MealTimeCard
                key={mealTime.id}
                mealTime={mealTime}
                index={index}
                appointmentId={appointmentId}
                isTubeFeeding={isTubeFeeding}
                isCompleted={isCompleted}
                isExpanded={expandedMealTimes[index] || false}
                onToggle={() => toggleMealTime(index)}
                planIndex={planIndex}
                mealTimes={mealTimes}
                crossPlanTargets={crossPlanTargets}
                externalMoveTargets={dietRecallMoveTargets}
                onActionsMenuOpen={handleActionsMenuOpen}
              />
            ))
          ) : (
            <Widget>
              <WidgetContent className="py-8 text-center text-muted-foreground">
                <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                {!currentFeedingType ? (
                  <>
                    <p className="z-body">No feeding type selected.</p>
                    <p className="z-caption mt-1">
                      Please select a feeding type in Nutrition Profile first.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="z-body">
                      No meal times configured for this plan.
                    </p>
                    <p className="z-caption mt-1">
                      Meal groups are being set up. Please try refreshing the
                      page.
                    </p>
                  </>
                )}
              </WidgetContent>
            </Widget>
          )}
        </div>
      )}

      {totalNutrients && hasFoodEntries && (
        <EnergySummaryCard
          totalNutrients={totalNutrients}
          isMealPlan={true}
          tdee={healthProfile?.tdee || 0}
          bmr={healthProfile?.bmr || 0}
          paCalories={healthProfile?.pa_calories || 0}
          targets={targets}
        />
      )}

      {totalNutrients && hasFoodEntries && (
        <FeaturedNutrientsCard totalNutrients={totalNutrients} />
      )}

      {totalNutrients && (
        <DetailedNutrientsPanel totalNutrients={totalNutrients} />
      )}

      <AddFeedDialog
        open={showAddFeed}
        onClose={() => setShowAddFeed(false)}
        appointmentId={appointmentId}
        isMealPlan={true}
        planIndex={planIndex}
      />
    </div>
  );
};

// =============================================================================
// MealPlanTab — top-level with Option 1/2/3 segmented control
// =============================================================================

// =============================================================================
// PlanTabTrigger — tab label with the plan's total kcal underneath.
// Reads cached meal-plan data so it shares queries with MealPlanContent.
// =============================================================================

const PlanTabTrigger: React.FC<{
  value: string;
  label: string;
  planIndex: number;
}> = ({ value, label, planIndex }) => {
  const { appointmentId, isTubeFeeding } = useNutritionEvaluationContext();
  const { data } = useMealPlanData(appointmentId, planIndex, isTubeFeeding);

  const energy = useMemo(() => {
    const nutrients = (data as MealData | FeedData | null)
      ?.total_nutrients as unknown as
      | { general?: Record<string, { amount: number }> }
      | undefined;
    return nutrients?.general?.Energy?.amount ?? 0;
  }, [data]);

  return (
    <TabsTrigger
      value={value}
      className="!flex-none h-auto px-4 py-1 gap-2 data-[state=active]:bg-card data-[state=active]:shadow-sm data-[state=active]:ring-1 data-[state=active]:ring-border"
    >
      <span className="whitespace-nowrap">{label}</span>
      <span className="rounded-full bg-foreground/5 px-2 py-0.5 z-caption text-muted-foreground whitespace-nowrap">
        {energy > 0 ? `${energy.toFixed(0)} kcal` : "—"}
      </span>
    </TabsTrigger>
  );
};

// =============================================================================
// CopyFromSourceButton — self-contained icon button + dialog. Reads its own
// queries (deduped via React Query cache with MealPlanContent's), so it can
// sit anywhere in the tree without prop drilling.
// =============================================================================

const extractMacros = (data: MealData | null | undefined) => {
  if (!data) return { energy: 0, protein: 0, carbs: 0, fat: 0 };
  const nutrients = data.total_nutrients as unknown as {
    general?: Record<string, { amount: number }>;
    carbohydrates?: Record<string, { amount: number }>;
    protein?: Record<string, { amount: number }>;
    lipids?: Record<string, { amount: number }>;
  };
  if (!nutrients) return { energy: 0, protein: 0, carbs: 0, fat: 0 };
  return {
    energy: nutrients.general?.Energy?.amount || 0,
    protein: nutrients.protein?.Protein?.amount || 0,
    carbs: nutrients.carbohydrates?.Carbohydrate?.amount || 0,
    fat: nutrients.lipids?.Fat?.amount || 0,
  };
};

const CopyFromSourceButton: React.FC<{ planIndex: number }> = ({
  planIndex,
}) => {
  const { appointmentId, isTubeFeeding, isCompleted } =
    useNutritionEvaluationContext();
  const [showCopyDialog, setShowCopyDialog] = useState(false);

  const { data: dietRecallData } = useDietRecall(appointmentId, isTubeFeeding);
  const { data: option1Data } = useMealPlanData(
    appointmentId,
    0,
    isTubeFeeding
  );

  const copyFromDietRecall = useCopyDietRecallToMealPlan(appointmentId);
  const copyFromOption = useCopyMealPlanOption(appointmentId);
  const isCopying = copyFromDietRecall.isPending || copyFromOption.isPending;

  const dietRecallNutrients = useMemo(
    () => extractMacros(dietRecallData as MealData | null),
    [dietRecallData]
  );
  const option1Nutrients = useMemo(
    () => extractMacros(option1Data as MealData | null),
    [option1Data]
  );

  const isOption1 = planIndex === 0;
  const copySourceLabel = isOption1 ? "Diet Recall" : "Plan A";
  const copySourceNutrients = isOption1
    ? dietRecallNutrients
    : option1Nutrients;
  const canCopyFromSource = copySourceNutrients.energy > 0;

  const handleCopyConfirm = () => {
    if (isOption1) {
      copyFromDietRecall.mutate(planIndex, {
        onSuccess: () => setShowCopyDialog(false),
      });
    } else {
      copyFromOption.mutate(
        { sourcePlanIndex: 0, targetPlanIndex: planIndex },
        { onSuccess: () => setShowCopyDialog(false) }
      );
    }
  };

  if (isCompleted || isTubeFeeding) return null;

  return (
    <>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon-sm"
              onClick={() => setShowCopyDialog(true)}
              disabled={!canCopyFromSource || isCopying}
              aria-label={`Copy from ${copySourceLabel}`}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Copy from {copySourceLabel}</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <CopySourceDialog
        open={showCopyDialog}
        onClose={() => setShowCopyDialog(false)}
        onConfirm={handleCopyConfirm}
        isLoading={isCopying}
        sourceLabel={copySourceLabel}
        sourceNutrients={copySourceNutrients}
      />
    </>
  );
};

export const MealPlanTab: React.FC = () => {
  const { selectedMealPlanIndex, setSelectedMealPlanIndex, isTubeFeeding } =
    useNutritionEvaluationContext();

  const planIndex = isTubeFeeding ? 0 : selectedMealPlanIndex;

  return (
    <ScrollArea className="h-full">
      <div className="space-y-3 p-1">
        {!isTubeFeeding && (
          <div className="flex items-center gap-2">
            <div className="flex-1" />
            <Tabs
              value={selectedMealPlanIndex.toString()}
              onValueChange={(value) =>
                setSelectedMealPlanIndex(parseInt(value))
              }
            >
              <TabsList>
                {PLAN_OPTIONS.map((option, idx) => (
                  <PlanTabTrigger
                    key={option.value}
                    value={option.value}
                    label={option.label}
                    planIndex={idx}
                  />
                ))}
              </TabsList>
            </Tabs>
            <div className="flex-1 flex justify-end">
              <CopyFromSourceButton planIndex={selectedMealPlanIndex} />
            </div>
          </div>
        )}

        <MealPlanContent planIndex={planIndex} />
      </div>
    </ScrollArea>
  );
};

export default MealPlanTab;
